import type { TemplateCategory } from '../hooks/useDocumentTemplates';

/**
 * Category-aware block generation for the document template editor.
 *
 * The backend exposes a different set of Handlebars variables per template
 * category (see `getAvailableVariables` in `document-templates.service.ts`).
 * A Quote uses `quote.total`, an Invoice uses `invoice.total`, a Statement has
 * no totals block at all, and so on. The visual/code editors must therefore
 * insert blocks whose variable references match the selected category — not a
 * hardcoded `invoice.*` prefix.
 *
 * This module centralises that per-category knowledge so both the VisualEditor
 * (block palette) and the EditorToolbar (code-mode insert buttons) stay in
 * sync with what the renderer actually provides.
 */

/**
 * Describes which variables a category exposes for the shared building blocks.
 *
 * - `entity` is the primary object prefix (e.g. `invoice`, `quote`, `advance`).
 * - `totals` maps the Subtotal / Tax / Total rows to concrete variable keys.
 *   When a field is `null` the corresponding row is omitted from the Totals
 *   block. When `totals` itself is `null` the category has no totals block.
 * - `notes` is the variable holding free-text notes, or `null` if none.
 * - `number` / dates power the Firm Header block's document meta section.
 * - `hasLineItems` controls whether the Line Items table / loop is offered.
 */
export interface CategoryBlockConfig {
  /** Human-readable document title used in the firm header (e.g. "QUOTE"). */
  title: string;
  /** Primary entity prefix used by the document (e.g. `invoice`, `quote`). */
  entity: string;
  /** Totals rows, or null when the category has no monetary totals. */
  totals: {
    subtotal: string | null;
    tax: string | null;
    total: string | null;
    totalLabel: string;
  } | null;
  /** Notes variable key, or null when the category has no notes field. */
  notes: string | null;
  /** Document number variable key, or null when there is none. */
  number: string | null;
  /** Primary date variable key shown in the header, or null. */
  primaryDate: { key: string; label: string } | null;
  /** Secondary date variable key shown in the header, or null. */
  secondaryDate: { key: string; label: string } | null;
  /** Whether a line items table / loop is meaningful for this category. */
  hasLineItems: boolean;
  /**
   * Whether the category resolves `billing_entity.*` tokens at render time
   * (i.e. the document is tied to a practice entity with a billing entity
   * config). Controls whether the "Billing Entity" block is offered.
   */
  supportsBillingEntity?: boolean;
}

/**
 * Per-category variable configuration. Keys mirror the backend
 * `getAvailableVariables` definitions so blocks reference variables that
 * actually resolve at render time.
 */
export const CATEGORY_BLOCK_CONFIG: Record<TemplateCategory, CategoryBlockConfig> = {
  invoice: {
    title: 'INVOICE',
    entity: 'invoice',
    totals: {
      subtotal: 'invoice.subtotal',
      tax: 'invoice.tax_amount',
      total: 'invoice.total',
      totalLabel: 'Total',
    },
    notes: 'invoice.notes',
    number: 'invoice.number',
    primaryDate: { key: 'invoice.issue_date', label: 'Date' },
    secondaryDate: { key: 'invoice.due_date', label: 'Due' },
    hasLineItems: true,
    supportsBillingEntity: true,
  },
  credit_note: {
    title: 'CREDIT NOTE',
    entity: 'credit_note',
    // Totals for credit notes come from the linked invoice; the direct credit
    // amount is the headline figure.
    totals: {
      subtotal: 'invoice.subtotal',
      tax: 'invoice.tax_amount',
      total: 'credit_note.amount',
      totalLabel: 'Credit Amount',
    },
    notes: 'credit_note.reason',
    number: 'credit_note.number',
    primaryDate: { key: 'credit_note.transaction_date', label: 'Date' },
    secondaryDate: null,
    hasLineItems: true,
    supportsBillingEntity: true,
  },
  debit_note: {
    title: 'DEBIT NOTE',
    entity: 'debit_note',
    totals: {
      subtotal: 'invoice.subtotal',
      tax: null,
      total: 'debit_note.amount',
      totalLabel: 'Debit Amount',
    },
    notes: 'debit_note.reason',
    number: 'debit_note.number',
    primaryDate: { key: 'debit_note.issue_date', label: 'Date' },
    secondaryDate: { key: 'debit_note.due_date', label: 'Due' },
    hasLineItems: false,
    supportsBillingEntity: true,
  },
  quote: {
    title: 'QUOTE',
    entity: 'quote',
    totals: {
      subtotal: 'quote.subtotal',
      tax: 'quote.tax_amount',
      total: 'quote.total',
      totalLabel: 'Total',
    },
    notes: 'quote.notes',
    number: 'quote.number',
    primaryDate: { key: 'quote.issue_date', label: 'Date' },
    secondaryDate: { key: 'quote.valid_until', label: 'Valid Until' },
    hasLineItems: true,
    supportsBillingEntity: true,
  },
  statement: {
    title: 'STATEMENT',
    entity: 'statement',
    // Statements use opening/closing balances rather than subtotal/tax/total.
    totals: {
      subtotal: 'statement.opening_balance',
      tax: null,
      total: 'statement.closing_balance',
      totalLabel: 'Closing Balance',
    },
    notes: null,
    number: null,
    primaryDate: { key: 'statement.start_date', label: 'From' },
    secondaryDate: { key: 'statement.end_date', label: 'To' },
    hasLineItems: false,
  },
  report: {
    title: 'REPORT',
    entity: 'report',
    totals: null,
    notes: null,
    number: null,
    primaryDate: { key: 'report.generated_at', label: 'Generated' },
    secondaryDate: null,
    hasLineItems: false,
  },
  letter: {
    title: 'LETTER',
    entity: 'letter',
    totals: null,
    notes: 'letter.body',
    number: 'letter.reference',
    primaryDate: { key: 'letter.date', label: 'Date' },
    secondaryDate: null,
    hasLineItems: false,
  },
  receipt: {
    title: 'RECEIPT',
    entity: 'receipt',
    totals: {
      subtotal: null,
      tax: null,
      total: 'receipt.amount',
      totalLabel: 'Amount Paid',
    },
    notes: null,
    number: 'receipt.number',
    primaryDate: { key: 'receipt.date', label: 'Date' },
    secondaryDate: null,
    hasLineItems: false,
  },
  write_off: {
    title: 'WRITE-OFF',
    entity: 'write_off',
    totals: {
      subtotal: 'invoice.subtotal',
      tax: 'invoice.tax_amount',
      total: 'write_off.amount',
      totalLabel: 'Write-Off Amount',
    },
    notes: 'write_off.reason',
    number: 'write_off.number',
    primaryDate: { key: 'write_off.transaction_date', label: 'Date' },
    secondaryDate: null,
    hasLineItems: true,
    supportsBillingEntity: true,
  },
  advance: {
    title: 'ADVANCE',
    entity: 'advance',
    totals: {
      subtotal: 'advance.subtotal',
      tax: 'advance.tax_amount',
      total: 'advance.total',
      totalLabel: 'Total',
    },
    notes: 'advance.reason',
    number: 'advance.number',
    primaryDate: { key: 'advance.transaction_date', label: 'Date' },
    secondaryDate: { key: 'advance.due_date', label: 'Due' },
    hasLineItems: true,
    supportsBillingEntity: true,
  },
};

/** Returns the block config for a category, defaulting to invoice. */
export function getCategoryConfig(category: TemplateCategory): CategoryBlockConfig {
  return CATEGORY_BLOCK_CONFIG[category] ?? CATEGORY_BLOCK_CONFIG.invoice;
}

// ─── Block HTML builders ───────────────────────────────────────────────────

/**
 * Builds the Firm Header block, using the category's document title, number
 * and date fields so a Quote shows "QUOTE / #{{quote.number}}" instead of
 * invoice fields.
 */
export function buildFirmHeaderHtml(cfg: CategoryBlockConfig): string {
  const numberLine = cfg.number
    ? `<p style="margin:6px 0 2px;font-size:12px;"><strong>#</strong> {{${cfg.number}}}</p>`
    : '';
  const primaryLine = cfg.primaryDate
    ? `<p style="margin:2px 0;font-size:12px;">${cfg.primaryDate.label}: {{formatDate ${cfg.primaryDate.key}}}</p>`
    : '';
  const secondaryLine = cfg.secondaryDate
    ? `<p style="margin:2px 0;font-size:12px;">${cfg.secondaryDate.label}: {{formatDate ${cfg.secondaryDate.key}}}</p>`
    : '';

  return `<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:16px;border-bottom:2px solid #4f46e5;margin-bottom:20px;"><div><h2 style="margin:0;font-size:18px;color:#1f2937;">{{firm.name}}</h2><p style="margin:4px 0 0;font-size:11px;color:#6b7280;">{{firm.address}}</p><p style="margin:2px 0 0;font-size:11px;color:#6b7280;">{{firm.phone}} | {{firm.email}}</p></div><div style="text-align:right;"><h1 style="margin:0;font-size:28px;color:#4f46e5;">${cfg.title}</h1>${numberLine}${primaryLine}${secondaryLine}</div></div>`;
}

/**
 * Builds the Totals block for the category, omitting rows the category does not
 * expose. Returns an empty string when the category has no totals.
 */
export function buildTotalsHtml(cfg: CategoryBlockConfig): string {
  if (!cfg.totals) return '';
  const rows: string[] = [];

  if (cfg.totals.subtotal) {
    rows.push(
      `<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:12px;"><span style="color:#6b7280;">Subtotal</span><span>{{currency ${cfg.totals.subtotal}}}</span></div>`,
    );
  }
  if (cfg.totals.tax) {
    rows.push(
      `<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:12px;"><span style="color:#6b7280;">Tax</span><span>{{currency ${cfg.totals.tax}}}</span></div>`,
    );
  }
  if (cfg.totals.total) {
    rows.push(
      `<div style="display:flex;justify-content:space-between;padding:10px 0;font-size:14px;font-weight:700;border-top:2px solid #1f2937;"><span>${cfg.totals.totalLabel}</span><span>{{currency ${cfg.totals.total}}}</span></div>`,
    );
  }

  return `<div style="display:flex;justify-content:flex-end;margin-top:16px;"><div style="width:220px;">${rows.join('')}</div></div>`;
}

/** Builds the Notes block for the category, or an empty string when unsupported. */
export function buildNotesHtml(cfg: CategoryBlockConfig): string {
  if (!cfg.notes) return '';
  return `<div style="margin-top:20px;padding:12px;background:#f9fafb;border-radius:6px;border:1px solid #e5e7eb;"><p style="margin:0 0 4px;font-size:11px;font-weight:600;color:#6b7280;">Notes</p><p style="margin:0;font-size:12px;color:#374151;">{{${cfg.notes}}}</p></div>`;
}

/**
 * Builds the Billing Entity Details block — the "from" business details of the
 * practice entity attached to the document (Settings → Billing → Practice
 * Entity → Billing Entity Details), resolved at render time via the
 * `billing_entity.*` tokens.
 *
 * Each field is wrapped in `{{#if}}` so a field the entity left blank prints
 * nothing (no empty label/line). Returns an empty string for categories that
 * do not resolve billing entity details. The user can delete any line they do
 * not want, or build their own layout from the individual tokens in the
 * variable picker — this block is just a ready-made starting point.
 */
export function buildBillingEntityHtml(cfg: CategoryBlockConfig): string {
  if (!cfg.supportsBillingEntity) return '';
  return (
    `<div style="margin-top:20px;padding:12px;background:#f9fafb;border-radius:6px;border:1px solid #e5e7eb;">` +
    `<p style="margin:0 0 6px;font-size:11px;font-weight:600;color:#6b7280;">Billing Entity</p>` +
    `{{#if billing_entity.name}}<p style="margin:0;font-size:12px;font-weight:600;color:#1f2937;">{{billing_entity.name}}</p>{{/if}}` +
    `{{#if billing_entity.address}}<p style="margin:2px 0 0;font-size:12px;color:#374151;">{{billing_entity.address}}</p>{{/if}}` +
    `{{#if billing_entity.email}}<p style="margin:2px 0 0;font-size:12px;color:#374151;">{{billing_entity.email}}</p>{{/if}}` +
    `{{#if billing_entity.phone}}<p style="margin:2px 0 0;font-size:12px;color:#374151;">{{billing_entity.phone}}</p>{{/if}}` +
    `{{#if billing_entity.business_number}}<p style="margin:2px 0 0;font-size:12px;color:#374151;">Business No: {{billing_entity.business_number}}</p>{{/if}}` +
    `{{#if billing_entity.payment_instructions}}<p style="margin:8px 0 0;font-size:11px;font-weight:600;color:#6b7280;">Payment Instructions</p><p style="margin:2px 0 0;font-size:12px;color:#374151;">{{billing_entity.payment_instructions}}</p>{{/if}}` +
    `</div>`
  );
}

/** Builds the footer bar. Only invoices expose payment terms. */
export function buildFooterBarHtml(cfg: CategoryBlockConfig): string {
  const paymentTerms =
    cfg.entity === 'invoice'
      ? `<p style="margin:4px 0 0;font-size:10px;color:#9ca3af;">Payment Terms: {{invoice.payment_terms_days}} days</p>`
      : '';
  return `<div style="text-align:center;padding-top:12px;border-top:1px solid #e5e7eb;margin-top:24px;"><p style="margin:0;font-size:10px;color:#9ca3af;">{{firm.name}} | {{firm.email}} | {{firm.phone}}</p>${paymentTerms}</div>`;
}

/** Builds the conditional (if notes) block, or a generic condition when no notes field exists. */
export function buildConditionHtml(cfg: CategoryBlockConfig): string {
  const field = cfg.notes ?? `${cfg.entity}.status`;
  const inner = cfg.notes
    ? `<p style="font-size:11px;font-weight:600;color:#6b7280;margin:0 0 4px;">Notes</p><p style="margin:0;font-size:12px;">{{${field}}}</p>`
    : `<p style="margin:0;font-size:12px;">{{${field}}}</p>`;
  return `{{#if ${field}}}\n<div style="margin-top:16px;padding:12px;background:#f9fafb;border-radius:6px;">\n  ${inner}\n</div>\n{{/if}}`;
}
